<template>
  <div class="absolute inset-0">

    <TransitionFade>
      <TypeformLoading v-if="computedLoading"/>
    </TransitionFade>

    <TransitionFade>
      <div v-if="showLogIn && !computedLoading"
           class="fixed inset-0 z-[200] flex flex-col items-center justify-center h-full w-full"
           :style="`background:${primaryBackground};color:${primaryForeground};`">
        <p class="font-bold text-3xl text-center max-w-md">Link Your Applications 🤝</p>
        <p class="text-lg text-center max-w-md mb-6 mt-2 opacity-60 font-medium">
          Have you <u>started applying</u> or <u>already applied</u> to another job on Business Draft? Link your
          applications by using the same email/password.
        </p>
        <BaseCard class="w-full max-w-md mx-auto bg-white rounded-2xl p-4 lg:p-6 mb-6 shadow-lg">
          <AuthLoginInline size="tall" class="w-full max-w-lg"
                           with-button
                           focus-password
                           :hex-background="primaryBackground"
                           :hex-foreground="primaryForeground"
                           :initial-email="initialEmail"
                           @logged-in="handleLoggedIn"/>
          <div class="lg:px-2 mt-2">
            <FormButton color="secondary" size="full" class="mt-2" @click="toggleShowLogIn">
              Cancel
            </FormButton>
          </div>
        </BaseCard>
      </div>
    </TransitionFade>

    <TransitionFade>
      <TypeformContent v-if="!showLogIn && !computedLoading"
                       :current-step="currentStep"
                       :total-steps="totalSteps"
                       @toggle-show-log-in="toggleShowLogIn">
        <slot/>
      </TypeformContent>
    </TransitionFade>

  </div>
</template>

<script setup>
import AuthLoginInline from "./AuthLoginInline.vue";
import BaseCard from "./BaseCard.vue";
import FormButton from "./FormButton.vue";
import TypeformLoading from "./TypeformLoading.vue";
import TransitionFade from "./TransitionFade.vue";
import TypeformContent from "./TypeformContent.vue";
import {computed, onBeforeUnmount, onMounted, provide, ref, watch} from "vue";

const emits = defineEmits(["update:showLogIn", "logged-in"]);
const props = defineProps({
  name: String,
  logo: String,
  avatar: String,
  hasLeft: Boolean,
  imageLeft: String,
  currentStep: Number,
  totalSteps: Number,
  leftComponent: [Object, Function],
  estimatedCompletionTime: String,
  primaryForeground: String,
  primaryBackground: String,
  secondaryForeground: String,
  secondaryBackground: String,
  initialEmail: String,
  enableLogIn: Boolean,
  showLogIn: Boolean,
  coerceGradients: Boolean,
  loading: Boolean,
});

const minLoadingPassed = ref(false);

const toggleShowLogIn = () => {
  if (!props.enableLogIn) return;
  emits("update:showLogIn", !props.showLogIn);
};

const computedLoading = computed(() => props.loading || !minLoadingPassed.value);

const minLoadingDuration = 1000;

provide("typeformData", {
  name: props.name,
  logo: props.logo,
  avatar: props.avatar,
  hasLeft: props.hasLeft,
  imageLeft: props.imageLeft,
  leftComponent: props.leftComponent,
  primaryForeground: props.primaryForeground,
  primaryBackground: props.primaryBackground,
  secondaryForeground: props.secondaryForeground,
  secondaryBackground: props.secondaryBackground,
  estimatedCompletionTime: props.estimatedCompletionTime,
  enableLogIn: props.enableLogIn,
});

if (props.coerceGradients) {
  provide("coerceGradients", {
    foregroundHex: props.primaryForeground,
    backgroundHex: props.primaryBackground,
  });
}

const listenToEscape = (e) => {
  if (e.key !== "Escape") return;
  e.preventDefault();
  e.stopPropagation();
  toggleShowLogIn();
};

const handleLoggedIn = () => {
  emits("update:showLogIn", false);
  emits("logged-in", false);
};

watch(() => props.showLogIn, (newVal, oldVal) => {
  if (newVal === oldVal) return;
  if (newVal) window.addEventListener("keydown", listenToEscape);
  else window.removeEventListener("keydown", listenToEscape);
}, {immediate: true});

onMounted(() => {
  setTimeout(() => {
    minLoadingPassed.value = true;
  }, minLoadingDuration);
});

onBeforeUnmount(() => {
  window.removeEventListener("keydown", listenToEscape);
});
</script>
